/**
 * Configuration Repository
 *
 * Life-critical configuration management with FERP repository pattern compliance:
 * - Safety framework integration for configuration operations
 * - Immutable audit trail for configuration changes
 * - Tenant isolation for configuration data
 * - Repository pattern compliance (NO direct API calls)
 * - Configuration validation and integrity checks
 */

import { BaseRepository } from './BaseRepository';

export interface ConfigurationContext {
    operation: 'LOAD_CONFIG' | 'UPDATE_CONFIG' | 'VALIDATE_CONFIG' | 'LOAD_MANIFEST';
    tenantId: string;
    criticalityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    configType?: 'MANIFEST' | 'APP_CONFIG' | 'FEATURE_FLAGS' | 'SYSTEM_CONFIG';
    source?: string;
}

export interface ManifestRoute {
    path: string;
    remote: string;
    element: string;
    elementVersion?: string;
    component?: string;
    framework?: string;
    scopes?: string[];
    props?: Record<string, unknown>;
}

export interface Manifest {
    app: string;
    version: string;
    routes: ManifestRoute[];
    menu: Array<{ label: string; to: string; scopes?: string[] }>;
    remotes: Record<string, { url: string }>;
}

export interface ConfigurationAuditEvent {
    operation: string;
    timestamp: number;
    tenantId: string;
    configType: string;
    configVersion?: string;
    safetyScore?: number;
    success: boolean;
    errorCode?: string;
    integrity?: {
        checksum: string;
        algorithm: string;
        verified: boolean;
    };
}

export class ConfigurationRepository extends BaseRepository<any> {
    private configCache = new Map<string, { data: any; timestamp: number; checksum: string }>();

    constructor() {
        super();
    }

    // Use BaseRepository's stubs; repository-specific logic doesn't override safety provider

    /**
     * Load plugin manifest with safety validation
     */
    async loadManifest(tenantId?: string): Promise<Manifest> {
        const resolvedTenantId = tenantId || this.tenant;

        const safetyContext: ConfigurationContext = {
            operation: 'LOAD_MANIFEST',
            tenantId: resolvedTenantId,
            criticalityLevel: 'HIGH',
            configType: 'MANIFEST',
            source: '/plugin-manifest.json'
        };

        // MANDATORY: Safety validation for configuration loading
        const safetyScore = await this.validateConfigSafety(safetyContext);

        if (safetyScore.overall < 65) { // Lower threshold for config but still validated
            await this.auditConfigEvent({
                operation: 'MANIFEST_LOAD_BLOCKED',
                timestamp: Date.now(),
                tenantId: resolvedTenantId,
                configType: 'MANIFEST',
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Manifest load blocked (score: ${safetyScore.overall})`);
        }

        // Repository pattern: Load manifest through GraphQL
        const auditContext = {
            operation: 'CONFIG_MANIFEST_LOAD',
            timestamp: Date.now(),
            tenantId: resolvedTenantId,
            metadata: {
                configType: 'MANIFEST',
                source: '/plugin-manifest.json',
                safetyScore: safetyScore.overall
            },
            safetyScore: safetyScore.overall
        };

        return this.executeWithConfigAudit(auditContext, async () => {
            const result = await this.query(
                `
                query LoadPluginManifest($tenantId: String!) {
                    pluginManifest(tenantId: $tenantId) {
                        app
                        version
                        routes {
                            path
                            remote
                            element
                            elementVersion
                            component
                            framework
                            scopes
                            props
                        }
                        menu {
                            label
                            to
                            scopes
                        }
                        remotes {
                            key
                            url
                        }
                        integrity {
                            checksum
                            algorithm
                            verified
                            lastChecked
                        }
                        auditId
                        loadedAt
                    }
                }
                `,
                { tenantId: resolvedTenantId },
                { cache: true, cacheTime: 5 * 60 * 1000 } // Cache for 5 minutes
            );

            // Validate configuration integrity
            await this.validateConfigIntegrity(result.pluginManifest, 'MANIFEST');

            // Transform to expected format
            return this.transformManifestResponse(result.pluginManifest);
        });
    }

    /**
     * Load application configuration with tenant isolation
     */
    async loadAppConfiguration(
        configType: string,
        tenantId?: string
    ): Promise<any> {
        const resolvedTenantId = tenantId || this.tenant;

        const safetyContext: ConfigurationContext = {
            operation: 'LOAD_CONFIG',
            tenantId: resolvedTenantId,
            criticalityLevel: 'MEDIUM',
            configType: configType as any
        };

        const safetyScore = await this.validateConfigSafety(safetyContext);

        if (safetyScore.overall < 60) {
            await this.auditConfigEvent({
                operation: 'CONFIG_LOAD_BLOCKED',
                timestamp: Date.now(),
                tenantId: resolvedTenantId,
                configType,
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Configuration load blocked (score: ${safetyScore.overall})`);
        }

        // Check cache first
        const cacheKey = `${configType}-${resolvedTenantId}`;
        const cached = this.configCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 10 * 60 * 1000) { // 10 minutes cache
            return cached.data;
        }

        // Repository pattern: Load configuration through GraphQL
        return this.query(
            `
            query LoadAppConfiguration($configType: String!, $tenantId: String!) {
                appConfiguration(configType: $configType, tenantId: $tenantId) {
                    type
                    version
                    data
                    environment
                    lastUpdated
                    updatedBy
                    integrity {
                        checksum
                        algorithm
                        verified
                    }
                    auditId
                }
            }
            `,
            { configType, tenantId: resolvedTenantId }
        );
    }

    /**
     * Update configuration with safety validation
     */
    async updateConfiguration(
        configType: string,
        configData: any,
        tenantId?: string
    ): Promise<any> {
        const resolvedTenantId = tenantId || this.tenant;

        const safetyContext: ConfigurationContext = {
            operation: 'UPDATE_CONFIG',
            tenantId: resolvedTenantId,
            criticalityLevel: 'HIGH',
            configType: configType as any
        };

        const safetyScore = await this.validateConfigSafety(safetyContext);

        if (safetyScore.overall < 75) { // Higher threshold for config updates
            await this.auditConfigEvent({
                operation: 'CONFIG_UPDATE_BLOCKED',
                timestamp: Date.now(),
                tenantId: resolvedTenantId,
                configType,
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Configuration update blocked (score: ${safetyScore.overall})`);
        }

        // Generate checksum for integrity
        const checksum = await this.generateChecksum(JSON.stringify(configData));

        // Repository pattern: Update configuration through mutation
        const result = await this.mutation(
            `
            mutation UpdateConfiguration(
                $configType: String!
                $configData: JSON!
                $tenantId: String!
                $checksum: String!
            ) {
                updateConfiguration(
                    configType: $configType
                    configData: $configData
                    tenantId: $tenantId
                    checksum: $checksum
                ) {
                    success
                    version
                    updatedAt
                    auditId
                    integrity {
                        checksum
                        verified
                    }
                }
            }
            `,
            {
                configType,
                configData: this.sanitizeConfigData(configData),
                tenantId: resolvedTenantId,
                checksum
            }
        );

        // Invalidate cache
        const cacheKey = `${configType}-${resolvedTenantId}`;
        this.configCache.delete(cacheKey);

        // Audit successful update
        await this.auditConfigEvent({
            operation: 'CONFIG_UPDATE_SUCCESS',
            timestamp: Date.now(),
            tenantId: resolvedTenantId,
            configType,
            configVersion: result.updateConfiguration.version,
            safetyScore: safetyScore.overall,
            success: true,
            integrity: {
                checksum,
                algorithm: 'SHA-256',
                verified: result.updateConfiguration.integrity.verified
            }
        });

        return result.updateConfiguration;
    }

    /**
     * Validate configuration integrity
     */
    async validateConfiguration(
        configType: string,
        configData: any,
        tenantId?: string
    ): Promise<any> {
        const resolvedTenantId = tenantId || this.tenant;

        const safetyContext: ConfigurationContext = {
            operation: 'VALIDATE_CONFIG',
            tenantId: resolvedTenantId,
            criticalityLevel: 'MEDIUM',
            configType: configType as any
        };

        await this.validateConfigSafety(safetyContext);

        // Repository pattern: Validate configuration through GraphQL
        return this.query(
            `
            query ValidateConfiguration(
                $configType: String!
                $configData: JSON!
                $tenantId: String!
            ) {
                validateConfiguration(
                    configType: $configType
                    configData: $configData
                    tenantId: $tenantId
                ) {
                    valid
                    errors {
                        field
                        message
                        severity
                    }
                    warnings {
                        field
                        message
                        severity
                    }
                    schema {
                        version
                        requiredFields
                    }
                    auditId
                }
            }
            `,
            { configType, configData, tenantId: resolvedTenantId }
        );
    }

    // Private helper methods

    /**
     * Validate configuration operation safety
     */
    private async validateConfigSafety(context: ConfigurationContext): Promise<any> {
        if (!this.safetyProvider) {
            throw new Error('CRITICAL: Safety provider not initialized for configuration');
        }

        return await this.safetyProvider.validateOperation({
            operation: context.operation,
            domain: 'CONFIGURATION',
            tenantId: context.tenantId,
            metadata: {
                criticalityLevel: context.criticalityLevel,
                configType: context.configType,
                source: context.source
            }
        });
    }

    /**
     * Execute configuration operation with audit trail
     */
    private async executeWithConfigAudit<T>(
        auditContext: any,
        operation: () => Promise<T>
    ): Promise<T> {
        await this.auditTrail.logOperationStart(auditContext);

        try {
            const result = await operation();

            await this.auditTrail.logOperationSuccess({
                ...auditContext,
                result: 'SUCCESS',
                completedAt: Date.now()
            });

            return result;
        } catch (error: unknown) {
            await this.auditTrail.logOperationFailure({
                ...auditContext,
                error: (error instanceof Error ? error.message : String(error)),
                failedAt: Date.now()
            });

            throw (error as Error);
        }
    }

    /**
     * Audit configuration events
     */
    private async auditConfigEvent(event: ConfigurationAuditEvent): Promise<void> {
        await this.auditTrail.logConfigurationEvent(event);
    }

    /**
     * Validate configuration integrity
     */
    private async validateConfigIntegrity(config: any, type: string): Promise<void> {
        if (!config) {
            throw new Error(`Invalid ${type} configuration: empty or null`);
        }

        // Check required fields based on configuration type
        if (type === 'MANIFEST') {
            const requiredFields = ['app', 'version', 'routes', 'menu', 'remotes'];
            const missingFields = requiredFields.filter(field => !(field in config));

            if (missingFields.length > 0) {
                throw new Error(`Invalid manifest: missing required fields: ${missingFields.join(', ')}`);
            }

            // Validate routes structure
            if (!Array.isArray(config.routes)) {
                throw new Error('Invalid manifest: routes must be an array');
            }

            // Validate menu structure
            if (!Array.isArray(config.menu)) {
                throw new Error('Invalid manifest: menu must be an array');
            }
        }

        // Verify integrity checksum if provided
        if (config.integrity && config.integrity.checksum) {
            const computedChecksum = await this.generateChecksum(JSON.stringify(config.data || config));
            if (computedChecksum !== config.integrity.checksum) {
                throw new Error(`Configuration integrity check failed: checksum mismatch`);
            }
        }
    }

    /**
     * Transform manifest response to expected format
     */
    private transformManifestResponse(response: any): Manifest {
        return {
            app: response.app,
            version: response.version,
            routes: response.routes || [],
            menu: response.menu || [],
            remotes: this.transformRemotesArray(response.remotes || [])
        };
    }

    /**
     * Transform remotes array to object format
     */
    private transformRemotesArray(remotes: Array<{ key: string; url: string }>): Record<string, { url: string }> {
        const result: Record<string, { url: string }> = {};
        remotes.forEach(remote => {
            result[remote.key] = { url: remote.url };
        });
        return result;
    }

    /**
     * Sanitize configuration data for transmission
     */
    private sanitizeConfigData(configData: any): any {
        // Remove sensitive data before transmission
        const sanitized = { ...configData };

        // Remove any fields that might contain sensitive information
        const sensitiveFields = ['password', 'secret', 'key', 'token', 'credential'];

        const sanitizeObject = (obj: any): any => {
            if (typeof obj !== 'object' || obj === null) {
                return obj;
            }

            if (Array.isArray(obj)) {
                return obj.map(item => sanitizeObject(item));
            }

            const result: any = {};
            for (const [key, value] of Object.entries(obj)) {
                if (sensitiveFields.some(sensitive => key.toLowerCase().includes(sensitive))) {
                    result[key] = '[REDACTED]';
                } else {
                    result[key] = sanitizeObject(value);
                }
            }
            return result;
        };

        return sanitizeObject(sanitized);
    }

    /**
     * Generate checksum for configuration integrity
     */
    private async generateChecksum(data: string): Promise<string> {
        try {
            const encoder = new TextEncoder();
            const dataBuffer = encoder.encode(data);
            const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (error) {
            // Fallback to simple hash for environments without crypto.subtle
            let hash = 0;
            for (let i = 0; i < data.length; i++) {
                const char = data.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }
            return hash.toString(16);
        }
    }

    // Repository pattern standard methods

    /**
     * Find configuration by tenant and type
     */
    async findByTenantAndType(tenantId: string, configType: string): Promise<any> {
        return this.loadAppConfiguration(configType, tenantId);
    }

    /**
     * Get all configurations for a tenant
     */
    async findByTenant(tenantId: string): Promise<any[]> {
        const safetyContext: ConfigurationContext = {
            operation: 'LOAD_CONFIG',
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateConfigSafety(safetyContext);

        return this.query(
            `
            query GetTenantConfigurations($tenantId: String!) {
                tenantConfigurations(tenantId: $tenantId) {
                    type
                    version
                    lastUpdated
                    updatedBy
                    environment
                    integrity {
                        checksum
                        verified
                    }
                }
            }
            `,
            { tenantId }
        );
    }

    /**
     * Get safety configuration for a specific domain context
     */
    async getSafetyConfiguration(domainContext: string): Promise<any> {
        const safetyContext: ConfigurationContext = {
            operation: 'LOAD_CONFIG',
            tenantId: this.tenant,
            criticalityLevel: 'HIGH',
            configType: 'SYSTEM_CONFIG',
            source: 'safety_configuration'
        };

        // MANDATORY: Safety validation for safety configuration loading
        const safetyScore = await this.validateConfigSafety(safetyContext);

        if (safetyScore.overall < 70) {
            await this.auditConfigEvent({
                operation: 'SAFETY_CONFIG_LOAD_BLOCKED',
                timestamp: Date.now(),
                tenantId: this.tenant,
                configType: 'SAFETY_CONFIG',
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Safety configuration load blocked (score: ${safetyScore.overall})`);
        }

        // Check cache first
        const cacheKey = `safety-${this.tenant}-${domainContext}`;
        const cached = this.configCache.get(cacheKey);

        if (cached && Date.now() - cached.timestamp < 300000) { // 5 minute cache
            return cached.data;
        }

        // Repository pattern: Load safety configuration through GraphQL
        const result = await this.query(
            `
            query GetSafetyConfiguration($tenantId: String!, $domainContext: String!) {
                safetyConfiguration(tenantId: $tenantId, domainContext: $domainContext) {
                    enabled
                    thresholds {
                        metric
                        value
                        operator
                        severity
                    }
                    rules {
                        id
                        name
                        condition
                        action
                        priority
                    }
                    checkpoints {
                        id
                        name
                        required
                        autoCheck
                    }
                    emergencyProtocols {
                        trigger
                        action
                        notificationChannels
                    }
                    monitoringInterval
                    alertingEnabled
                    autoInterventionEnabled
                }
            }
            `,
            {
                tenantId: this.tenant,
                domainContext
            }
        );

        // Cache the result
        if (result.safetyConfiguration) {
            const checksum = await this.generateChecksum(JSON.stringify(result.safetyConfiguration));
            this.configCache.set(cacheKey, {
                data: result.safetyConfiguration,
                timestamp: Date.now(),
                checksum
            });
        }

        // Audit successful load
        await this.auditConfigEvent({
            operation: 'SAFETY_CONFIG_LOAD_SUCCESS',
            timestamp: Date.now(),
            tenantId: this.tenant,
            configType: 'SAFETY_CONFIG',
            safetyScore: safetyScore.overall,
            success: true
        });

        return result.safetyConfiguration || {
            enabled: true,
            thresholds: [],
            rules: [],
            checkpoints: [],
            emergencyProtocols: [],
            monitoringInterval: 5000,
            alertingEnabled: true,
            autoInterventionEnabled: false
        };
    }
}

// Singleton instance for configuration management
export const configurationRepository = new ConfigurationRepository();